import React, {
    CSSProperties,
    Dispatch,
    Fragment,
    KeyboardEvent as ReactKeyboardEvent,
    MutableRefObject,
    PropsWithChildren,
    Reducer,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState
} from "react";

// @ts-expect-error
import { css, CssObject } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { MotionContainerProps, indexOf } from "./components/motion-container";
import GlobalNavLink from "./nav-link";

export type DropdownStateProps = {
    readonly currentOpenDropDown: string;
    readonly setCurrentOpenDropDown: React.Dispatch<
        React.SetStateAction<string>
    >;
};

const DropdownContentStyle = css({
    display: "none",
    opacity: 1,
    insetBlockStart: "100%",
    minWidth: "264px",
    width: "max-content",
    position: "absolute",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    marginInline: "auto",
    overflow: "hidden",
    borderRadius: "15px",
    border: "none",
    boxShadow: "0 0 1px 0 #ccc, 0 5px 20px -5px rgba(0,0,0,0.1)",
    order: 100,
    backgroundColor: "#ffffff",
    transition: "width 200ms ease",
    zIndex: 10,
    animation: "fadein 0.25s 1"
});

export enum SubnavLayout {
    Product = 4,
    Teams = 3,
    WhyAtlassian = 3,
    Resources = 4
}

export type GlobalNavLinkContentProps = {
    readonly globalLinksKey: string;
    readonly linkText: string;
    readonly hasSubnav: boolean;
    readonly isActive?: boolean;
    readonly href?: string;
    readonly productKey?: string;
    readonly content?: JSX.Element | string;
    readonly layout?: SubnavLayout;
    readonly containerPosition?: CSSProperties["position"];
    readonly containerStyles?: CssObject<unknown> | CssObject<unknown>[];
};

function layoutToWidth(layout: SubnavLayout | undefined) {
    switch (layout) {
        case 4:
            return "57rem";
        case 3:
            return "43.5rem";
        default:
            return "auto";
    }
}

type GlobalNavState = {
    current: string | null;
    prev: string | null;
};

type GlobalNavContextType = GlobalNavState & {
    itemList: Array<string>;
    direction: MotionContainerProps["direction"];
    setCurrent: Dispatch<string | null>;
    dropdownRef: MutableRefObject<HTMLDivElement | null>;
};
const GlobalNavContext = createContext<GlobalNavContextType | null>(null);

export const GlobalNavProvider = ({
    itemList,
    children
}: PropsWithChildren<{ itemList: Array<string> }>) => {
    const dropdownRef = useRef(null);

    const [state, dispatch] = useReducer<
        Reducer<GlobalNavState, string | null>
    >(
        function reducer(state, action) {
            const prev = state.current;

            return { ...state, current: action, prev };
        },
        { current: null, prev: null }
    );

    const { current, prev } = state;

    const value = useMemo(() => {
        const currentIndex = indexOf(itemList, (link) => {
            return link === current;
        });

        const prevIndex = indexOf(itemList, (link) => {
            return link === prev;
        });

        const direction: MotionContainerProps["direction"] =
            prevIndex === -1
                ? "up"
                : prevIndex > currentIndex
                ? "right"
                : "left";

        return {
            current,
            prev,
            itemList,
            direction,
            setCurrent: dispatch,
            dropdownRef
        };
    }, [current, prev, itemList, dropdownRef]);

    return (
        <GlobalNavContext.Provider value={value}>
            {children}
        </GlobalNavContext.Provider>
    );
};

export const useCurrentNav = () => {
    const context = useContext(GlobalNavContext);

    if (!context) {
        throw new Error("useCurrentNav must be used within GlobalNavProvider");
    }

    return context;
};

export const useExitCurrentNavOnHover = () => {
    const { setCurrent } = useCurrentNav();

    const [isHovered, setIsHovered] = useState(true);

    const onMouseLeave = () => {
        setCurrent("");
        setIsHovered(false);
    };

    const onMouseEnter = () => {
        setIsHovered(true);
    };

    return { onMouseEnter, onMouseLeave, isHovered };
};

export const DivOnHoverExit = ({
    children
}: {
    children: React.ReactElement;
}) => {
    const { isHovered, ...divProps } = useExitCurrentNavOnHover();

    return <div {...divProps}>{children}</div>;
};

const MoreOrLess = ({
    showButton,
    isShowingMore,
    onClick
}: {
    showButton: boolean;
    onClick: () => void;
    isShowingMore?: boolean;
}) => {
    const intl = useIntl();
    const visibility: CSSProperties["visibility"] = showButton
        ? "visible"
        : "hidden";

    const onKeyDown = (e: ReactKeyboardEvent) => {
        if (e.code === "Enter" || e.code === "Space") {
            onClick();
        }
    };

    return (
        <li
            aria-hidden={true}
            style={{
                visibility,
                position: showButton ? "initial" : "absolute"
            }}
            data-element-globalnav={isShowingMore ? "less" : "more"}
            data-element-visibility={visibility}
        >
            <span
                role="button"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={onKeyDown}
                className="globalnav-more-less"
                css={css({
                    "display": "flex",
                    "alignItems": "center",
                    "lineHeight": "1rem",
                    "marginBlockEnd": "-1px",
                    "cursor": "pointer",
                    "&:hover": {
                        color: "#0747A6"
                    }
                })}
            >
                {isShowingMore ? (
                    <span css={css({ color: "#7D818A" })}>
                        {intl.formatMessage({
                            id: "globalNav.V2.navlink.lessMinus",
                            defaultMessage: "Less -"
                        })}
                    </span>
                ) : (
                    <span>
                        {intl.formatMessage({
                            id: "globalNav.V2.navlink.morePlus",
                            defaultMessage: "More +"
                        })}
                    </span>
                )}
            </span>
        </li>
    );
};

const GlobalNavLinksContainer = ({
    globalLinksProps,
    globalLinksRef,
    maxLinks = globalLinksProps.length,
    showMore,
    setShowMore
}: {
    globalLinksProps: GlobalNavLinkContentProps[];
    globalLinksRef?: MutableRefObject<HTMLUListElement | null>;
    maxLinks?: number;
    showMore?: boolean;
    setShowMore?: Dispatch<SetStateAction<boolean>>;
}) => {
    const { current, setCurrent, dropdownRef } = useCurrentNav();
    const closedDropdownRef = useRef<HTMLDivElement | null>(null);

    const handleEscKeyEvent = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setCurrent("");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setCurrent("");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        // Attach the listeners on component mount.
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscKeyEvent, false);
        // Detach the listeners on component unmount.
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscKeyEvent, false);
        };
    }, [handleEscKeyEvent, handleClickOutside]);

    const showMoreButton = maxLinks < globalLinksProps.length;

    return (
        <>
            <ul
                className="navigation-links"
                css={css({
                    display: "grid",
                    gridTemplateColumns: "repeat(5, max-content)",
                    gap: token("space.400"),
                    alignItems: "end",
                    listStyle: "none",
                    margin: 0,
                    padding: 0,
                    [media.above.md]: {
                        marginInlineStart: token("space.400")
                    }
                })}
                style={{
                    gridTemplateColumns: `repeat(${
                        showMore
                            ? globalLinksProps.length + 1
                            : maxLinks === globalLinksProps.length
                            ? maxLinks
                            : maxLinks + 1
                    }, max-content)`
                }}
                ref={globalLinksRef}
            >
                {globalLinksProps.map(
                    (globalLink: GlobalNavLinkContentProps, index) => {
                        const containerPosition =
                            globalLink.containerPosition || "initial";
                        const isVisible = globalLink.globalLinksKey === current;

                        return (
                            <Fragment key={globalLink.globalLinksKey}>
                                <li
                                    className="nav-link"
                                    style={{
                                        position:
                                            index + 1 <= maxLinks || showMore
                                                ? containerPosition
                                                : "absolute",
                                        visibility:
                                            index + 1 <= maxLinks || showMore
                                                ? "visible"
                                                : "hidden"
                                    }}
                                >
                                    <GlobalNavLink {...globalLink} />
                                    <div
                                        css={[
                                            DropdownContentStyle,
                                            isVisible &&
                                                css({ display: "block" })
                                        ]}
                                        className="dropdown-content"
                                        ref={
                                            isVisible
                                                ? dropdownRef
                                                : closedDropdownRef
                                        }
                                        aria-hidden={!globalLink?.content}
                                        style={{
                                            visibility: !!globalLink?.content
                                                ? "visible"
                                                : "hidden",
                                            opacity: !!globalLink?.content
                                                ? 1
                                                : 0,
                                            width: layoutToWidth(
                                                globalLink?.layout
                                            ),
                                            ...globalLink?.containerStyles
                                        }}
                                    >
                                        <DropdownContentWrapper>
                                            {globalLink.content}
                                        </DropdownContentWrapper>
                                    </div>
                                </li>
                                {globalLink.globalLinksKey === current &&
                                    globalLink.hasSubnav && (
                                        <div
                                            className="overlay"
                                            css={css({
                                                position: "fixed",
                                                display: "block",
                                                width: "100%",
                                                height: "100%",
                                                insetBlockStart: "0",
                                                insetInlineStart: "0",
                                                insetInlineEnd: "0",
                                                insetBlockEnd: "0",
                                                backgroundColor:
                                                    "rgba(255,255,255,0.7)",
                                                zIndex: "-1"
                                            })}
                                        />
                                    )}
                            </Fragment>
                        );
                    }
                )}
                {!!setShowMore && (
                    <MoreOrLess
                        showButton={showMoreButton}
                        isShowingMore={showMore}
                        onClick={() => setShowMore((prev) => !prev)}
                    />
                )}
            </ul>
        </>
    );
};

export const DropdownContentWrapper = ({ children }: PropsWithChildren) => (
    <div className="inner-dropdown">{children}</div>
);

export default GlobalNavLinksContainer;
